import React, { useEffect, useState } from 'react';
import {
    Box, TextField, Typography, IconButton, Stack, Divider
} from '@mui/material';
import ReactQuill from 'react-quill';
import { v4 as uuidv4 } from 'uuid';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate, useParams } from 'react-router-dom';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { doc, getDoc, Timestamp, updateDoc } from 'firebase/firestore';
import { db } from '../../../firebase.config';
import Iconify from '../../../components/Iconify';
import CustomButton from '../../../components/CustomButton';
import { useSnackbar } from '../../../context/snackbarContext';

const SortableItem = ({ id, handle, children }) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        marginBottom: '16px'
    };

    return (
        <div ref={setNodeRef} style={style}>
            <div {...attributes} {...listeners} style={{ cursor: 'grab', marginBottom: 8 }}>
                {handle}
            </div>
            {children}
        </div>
    );
};

const ArticleEditor = () => {
    const { publique, collectionName, id } = useParams();
    const [title, setTitle] = useState('');
    const [intro, setIntro] = useState();
    const [loading, setLoading] = useState(false);
    const [sections, setSections] = useState([]);
    const navigate = useNavigate();
    const showSnackbar = useSnackbar()

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const addSection = () => {
        setSections([...sections, {
            id: uuidv4(),
            title: '',
            content: '',
            media: [],
            subsections: []
        }]);
    };

    const updateSection = (index, key, value) => {
        const updated = [...sections];
        updated[index][key] = value;
        setSections(updated);
    };

    const addSubsection = (sectionIndex) => {
        const updated = [...sections];
        updated[sectionIndex].subsections.push({
            id: uuidv4(),
            title: '',
            content: '',
            media: []
        });
        setSections(updated);
    };

    const updateSubsection = (sectionIndex, subIndex, key, value) => {
        const updated = [...sections];
        updated[sectionIndex].subsections[subIndex][key] = value;
        setSections(updated);
    };

    const handleDeleteSubsection = (sectionIndex, subIndex) => {
        const updated = [...sections];
        updated[sectionIndex].subsections.splice(subIndex, 1);
        setSections(updated);
    };

    const handleSave = async () => {
        try {
            setLoading(true)
            const article = {
                title,
                createdAt: new Date().toISOString(),
                sections,
            };
            console.log('Article à sauvegarder :', article);
            // rattache la campagne et le template
            const statRef = doc(db, `resources/${publique}/articles`, `${id}`);

            await updateDoc(statRef, {
                name: title,
                sections,
                updatedDate: new Date(),
            });
            showSnackbar('Article a été mise a jour avec succes', false)
        } catch (error) {
            console.log(error)
            showSnackbar('Erreur lors de la confirmation de la campagne', true)
        } finally {
            setLoading(false)
        }

        // ici tu envoies dans Firestore
    };

    const back = () => {
        try {
            navigate(-1);
        } catch (error) {
            console.log(error);
        }
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (active.id !== over.id) {
            const oldIndex = sections.findIndex(item => item.id === active.id);
            const newIndex = sections.findIndex(item => item.id === over.id);
            setSections((items) => arrayMove(items, oldIndex, newIndex));
        }
    };


    const isValidCollection = (name) => /^[a-zA-Z]+$/.test(name);

    useEffect(() => {
        const fetchArticle = async () => {
            if (!id || !isValidCollection(collectionName)) {
                navigate(-1, { replace: true })
            };

            try {
                console.log(collectionName)
                const docRef = doc(db, `resources/${publique}/articles`, id);
                const docSnap = await getDoc(docRef);

                if (docSnap.exists()) {
                    const data = docSnap.data();
                    setTitle(data?.name || '');
                    setSections(data?.sections || []);
                } else {
                    console.log('Aucun article trouvé');
                }
            } catch (err) {
                console.error('Erreur de chargement de l’article:', err);
            }
        };

        fetchArticle();
    }, [collectionName, id]);

    return (
        <Box>
            <Stack direction='row' justifyContent='space-between' sx={{ flex: '0 0 auto', padding: 2, backgroundColor: '#f5f5f5' }}>
                <CustomButton loading={loading} startIcon={<Iconify icon="material-symbols:arrow-back-ios" />} type='secondaire' buttonText='retour' onClick={back} />
                <CustomButton loading={loading} type='primaire' buttonText='Enregistrer' onClick={handleSave} />
            </Stack>
            <Box sx={{ p: 3 }}>
                <TextField
                    label="Titre de l'article"
                    fullWidth
                    sx={{ mb: 3 }}
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                />
                <ReactQuill
                    theme="snow"
                    placeholder='Saisir'
                    value={intro}
                    onChange={(value) => setIntro(value)}
                />

                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={sections.map(s => s.id)} strategy={verticalListSortingStrategy}>
                        {sections.map((section, i) => (
                            <SortableItem
                                key={section.id}
                                id={section.id}
                                handle={<Typography variant="subtitle2" color="text.secondary">⇅ Déplacer la section {i + 1}</Typography>}
                            >
                                <Box sx={{ border: '1px solid #ccc', p: 2, borderRadius: 2, backgroundColor: '#fff' }}>
                                    <TextField
                                        label="Titre"
                                        fullWidth
                                        sx={{ my: 1 }}
                                        value={section.title}
                                        onChange={(e) => updateSection(i, 'title', e.target.value)}
                                    />
                                    <ReactQuill
                                        theme="snow"
                                        value={section.content}
                                        onChange={(value) => updateSection(i, 'content', value)}
                                    />
                                    <Stack direction="row" spacing={2} sx={{ my: 2 }}>
                                        <CustomButton loading={loading} type='secondaire' buttonText='Ajouter une sous-section' startIcon={<AddIcon />} onClick={() => addSubsection(i)} />
                                        <IconButton onClick={() => setSections(sections.filter((_, idx) => idx !== i))}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </Stack>

                                    {section.subsections.map((sub, j) => (
                                        <Box key={sub.id} sx={{ mt: 2, p: 2, backgroundColor: '#f9f9f9', borderRadius: 2 }}>
                                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                <Typography variant="subtitle1">Sous-section {j + 1}</Typography>
                                                <IconButton onClick={() => handleDeleteSubsection(i, j)}>
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Stack>
                                            <TextField
                                                label="Titre"
                                                fullWidth
                                                sx={{ my: 1 }}
                                                value={sub.title}
                                                onChange={(e) => updateSubsection(i, j, 'title', e.target.value)}
                                            />
                                            <ReactQuill
                                                theme="snow"
                                                value={sub.content}
                                                onChange={(value) => updateSubsection(i, j, 'content', value)}
                                            />
                                        </Box>
                                    ))}
                                </Box>
                            </SortableItem>
                        ))}
                    </SortableContext>
                </DndContext>

                <CustomButton loading={loading} type='primaire' buttonText='Ajouter une section' startIcon={<AddIcon />} onClick={addSection} />
                <Divider sx={{ my: 4 }} />
            </Box>
        </Box>
    );
};

export default ArticleEditor;
